<template>
  <div class="player-media-full">
    <div
      v-if="path.audio_only"
      class="player-media-full player-media-splash"
      :style="{
        backgroundImage: !!path.poster ? 'url(' + path.poster + ')' : 'none'
      }"
    >
      <audio class="player player-media-full" controls></audio>
    </div>
    <video
      v-else
      class="player player-media-full"
      controls
      crossorigin
      playsinline
    >
      <track
        v-for="(sub, index) in path.subtitles"
        :key="index"
        kind="captions"
        :label="sub.label"
        :src="sub.src"
        :srclang="sub.srclang.toLowerCase()"
        :default="sub.deftrack"
      />
    </video>
    <DonateButton ref="donateButton" v-if="this.donate" />
    <ChromecastButton
      ref="chromecastButton"
      @enableChromecast="enableChromecast"
      v-if="chromecastEnabled"
    />
  </div>
</template>

<script>
import { mapState, mapGetters, mapMutations } from "vuex";
import DonateButton from "@/components/DonateButton.vue";
import ChromecastButton from "@/components/ChromecastButton.vue";

import axios from "axios";

import Plyr from "plyr/dist/plyr.polyfilled.js";
import "plyr/dist/plyr.css";

import Hls from "hls.js";

export default {
  name: "PlayerMedia",
  components: {
    DonateButton,
    ChromecastButton
  },
  data: function() {
    return {
      hls: null,
      plyr: null,
      playlistURL: null,
      isPaused: false
    };
  },
  mounted() {
    this.loadPlaylist();
  },
  beforeDestroy() {
    this.shutdown();
  },
  computed: {
    ...mapState("player", ["path", "pos", "donate", "chromecastEnabled"]),
    ...mapGetters("player", ["sourceURL"])
  },
  methods: {
    ...mapMutations("player", { setLoading: "UPDATE_LOADING" }),
    enableChromecast() {
      const media = {
        playlistURL: this.playlistURL
      };

      this.$emit("enableChromecast", media);
    },
    shutdown() {
      if (this.hls) {
        this.hls.destroy();
        this.hls = null;
      }

      if (this.plyr) {
        this.plyr.destroy();
        this.plyr = null;
      }
    },
    loadPlaylist() {
      this.setLoading(true);

      axios
        .get(this.sourceURL)
        .then(response => {
          this.playlistURL = response.data.stc.m3u8[0].url;

          if (this.playlistURL.indexOf("sidgen") != -1) {
            this.playlistURL +=
              (this.playlistURL.indexOf("?") == -1 ? "?" : "&") + "sidgen=json";
            axios
              .get(this.playlistURL)
              .then(response => {
                this.playlistURL = response.data.url;
                this.player();
              })
              .catch(error => {
                this.$emit("error", error.message);
                console.log(error);
              });
          } else {
            this.player();
          }
        })
        .catch(error => {
          this.$emit("error", error.message);
          console.log(error);
        });
    },
    player() {
      this.setLoading(true);

      const video = document.querySelector(".player");

      const plyrConfig = {
        iconUrl: require("plyr/dist/plyr.svg"),
        fullscreen: {
          enabled: true,
          fallback: true,
          iosNative: true
        }
      };
      if (this.path.is_live) {
        plyrConfig.duration = Infinity;
      }

      if (this.path.audio_only) {
        plyrConfig.settings = [];
      }

      if (this.path.subtitles) {
        const sublen = this.path.subtitles.length;
        for (let i = 0; i < sublen; ++i) {
          if (this.path.subtitles[i].deftrack) {
            plyrConfig.captions = {
              active: true,
              language: this.path.subtitles[i].srclang.toLowerCase()
            };
            break;
          }
        }
      }

      /* eslint-disable no-unused-vars */
      this.plyr = new Plyr(video, plyrConfig);
      /* eslint-enable no-unused-vars */

      this.plyr.on("ended", () => {
        this.$emit("ended");
      });

      if (this.path.is_live) {
        this.plyr.on("pause", () => {
          this.isPaused = true;
        });

        this.plyr.on("play", () => {
          if (this.isPaused) {
            this.setLoading(true);
            setTimeout(() => {
              this.shutdown();
              this.player();
            }, 100);
          }
          this.isPaused = false;
        });
      }

      if (!this.path.is_live && this.path.pos_update) {
        this.plyr.on("timeupdate", async () => {
          const pos = this.plyr.currentTime;

          if (isNaN(pos) || pos < 0) {
            return;
          }

          const newPos = Math.round(pos);
          if (Math.abs(newPos - this.pos) < 10) {
            return;
          }

          await this.$store.dispatch("player/setPos", newPos);
        });
      }

      this.plyr.once("playing", () => {
        this.setLoading(false);
      });

      if (!this.path.is_live && this.path.pos > 0 && !isNaN(this.path.pos)) {
        this.plyr.once("playing", () => {
          const pos = Math.round(parseFloat(this.path.pos) * 10) / 10;
          this.plyr.currentTime = pos;
        });
      }

      if (this.donate) {
        const donate = this.$refs.donateButton.$el;
        //const donate = document.getElementById("donate");
        this.plyr.elements.container.appendChild(donate);
      }

      if (this.chromecastEnabled) {
        const plyrFullscreen = this.plyr.elements.buttons["fullscreen"];
        const cc = this.$refs.chromecastButton.$el;
        plyrFullscreen.parentNode.insertBefore(cc, plyrFullscreen);
        //this.plyr.elements.controls.appendChild(cc);
      }

      if (!Hls.isSupported()) {
        video.addEventListener("loadedmetadata", () => {
          this.plyr.play();
        });
        video.addEventListener("error", () => {
          if (video.error && video.error.code == 4) {
            this.$emit("notFound");
          } else {
            this.$emit("error");
          }
        });
        video.src = this.playlistURL;
      } else {
        this.hls = new Hls({});
        this.hls.on(Hls.Events.MANIFEST_PARSED, () => {
          this.plyr.play();
        });

        this.hls.on(Hls.Events.ERROR, (event, data) => {
          console.log(data);
          if (data.fatal) {
            switch (data.type) {
              case Hls.ErrorTypes.NETWORK_ERROR:
                switch (data.details) {
                  case Hls.ErrorDetails.MANIFEST_LOAD_ERROR:
                  case Hls.ErrorDetails.MANIFEST_LOAD_TIMEOUT:
                    this.$emit("notFound");
                    break;
                  default:
                    this.$emit("error");
                    break;
                }
                break;
              case Hls.ErrorTypes.MEDIA_ERROR:
                this.$emit("error");
                break;
              default:
                this.$emit("error");
                break;
            }
          }
        });

        this.hls.loadSource(this.playlistURL);
        this.hls.attachMedia(video);
      }
    }
  }
};
</script>

<style lang="scss">
:root {
  --disconnected-color: #fff;
  --plyr-color-main: #fff;
  --plyr-audio-controls-background: linear-gradient(
    rgba(0, 0, 0, 0),
    rgba(0, 0, 0, 0.75)
  );
  --plyr-video-control-background-hover: #616e82;
  --plyr-audio-control-color: #ffffff;
  --plyr-audio-control-background-hover: #616e82;
}

.player-media-full {
  width: 100%;
  height: 100%;
}

.player-media-splash {
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center center;
}

.plyr__control--overlaid {
  border-radius: 5px !important;
  border: 1px solid #fff !important;
}

.plyr__control--overlaid,
.plyr__control--overlaid:hover {
  background: rgba(0, 0, 0, 0.25) !important;
}
/*
.plyr.plyr--stopped .plyr__controls,
.plyr.plyr--stopped .plyr__control--overlaid {
  display: none;
}
*/

// Temp fix till plyr fixes internally
.plyr {
  height: 100%;
}

.plyr--audio {
  width: 100%;
  height: 100%;
}

.plyr--audio .plyr__controls {
  position: absolute !important;
  bottom: 0 !important;
  right: 0 !important;
  left: 0 !important;
}
</style>
